import { Component, inject } from '@angular/core';
import { HlAreaComponent } from './hl-area.component';
import { AltNeu, EinbindungEbene, Hanganschluss, MosaikBereich, Neigungsstufe, Woelbung, trackByIndex } from 'src/app/data-model';
import { DataService, wuchsbezirkFilter } from 'src/app/data.service';
import { WeatherService, OfflineModeService } from '@luzula/luzula-lib';
import { TransformationService } from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';

// Neigungsstufen-Grenzen für automatische Zuordnung
const NEIGUNGSSTUFEN_GRENZEN: { stufe: Neigungsstufe; minGrad: number; maxGrad: number }[] = [
	{ stufe: 'Ebene', minGrad: 0, maxGrad: 1 },
	{ stufe: 'schwächer geneigter Flachhang', minGrad: 1, maxGrad: 3 },
	{ stufe: 'stärker geneigter Flachhang', minGrad: 3, maxGrad: 7 },
	{ stufe: 'schwächer geneigter Lehnhang', minGrad: 7, maxGrad: 15 },
	{ stufe: 'stärker geneigter Lehnhang', minGrad: 15, maxGrad: 25 },
	{ stufe: 'schwächer geneigter Steilhang', minGrad: 25, maxGrad: 60 },
	{ stufe: 'stärker geneigter Steilhang', minGrad: 60, maxGrad: 90 }
];

@Component({
    standalone: false,
    selector: '',
    templateUrl: './naturraum.component.html',
    styleUrls: ['./hl-area.component.css'],
    
})
export class NaturraumComponent extends HlAreaComponent {
  trackByIndex = trackByIndex;
	wuchsbezirkFilter = wuchsbezirkFilter;
	weatherLoading = false;

	private weatherService = inject(WeatherService);
	private transformationService = inject(TransformationService);
	private toastr = inject(ToastrService);
	private dataService = inject(DataService);
	private offlineMode = inject(OfflineModeService);

	async fetchWeather(useCurrentWeather = false) {
		if (this.offlineMode.isOffline() || !this.offlineMode.isNetworkOnline()) {
			this.toastr.info('Witterung im Offline-Modus nicht verfügbar');
			return;
		}

		if (!this.data?.header?.koordinaten) {
			this.toastr.warning('Keine Koordinaten vorhanden');
			return;
		}

		const latlng = this.transformationService.from(this.data.header.koordinaten);
		if (!latlng || !latlng.lat || !latlng.lng) {
			this.toastr.warning('Ungültige Koordinaten');
			return;
		}

		this.weatherLoading = true;

		try {
			let weather;
			if (useCurrentWeather) {
				weather = await this.weatherService.getCurrentWeather(latlng.lat, latlng.lng);
			} else {
				// Historisches Wetter für Aufnahmedatum
				const datum = this.data.header?.aufnahme?.datum;
				if (!datum) {
					this.toastr.warning('Kein Aufnahmedatum vorhanden');
					this.weatherLoading = false;
					return;
				}
				const dateStr = this.weatherService.formatDateForApi(datum);
				weather = await this.weatherService.getHistoricalWeather(latlng.lat, latlng.lng, dateStr);
			}

			if (weather) {
				const description = this.weatherService.describeWeather(weather);
				this.data.extendedHeader.witterung = description.full;

				// Rohdaten speichern für spätere Neuinterpretation
				const config = this.weatherService.getConfig();
				this.data.extendedHeader.witterungRohdaten = {
					temperature: weather.temperature,
					precipitation: weather.precipitation,
					cloudCover: weather.cloudCover,
					weatherCode: weather.weatherCode,
					windSpeed: weather.windSpeed,
					windDirection: weather.windDirection,
					humidity: weather.humidity,
					precipitationDays: weather.precipitationLast3Days,
					daysUsed: config.soil.tage,
					fetchedAt: new Date().toISOString(),
					isCurrentWeather: useCurrentWeather
				};

				// Der Knopf schreibt am Eingabefeld vorbei; die Marke „geaendert"
				// setzt sonst nur ein Feld, in das jemand tippt. Ohne sie erschien
				// kein Speichern-Knopf, und beim Verlassen wurde nicht gefragt
				// (Nutzer-Befund 15.09.2026: „wird nicht immer gespeichert").
				this.vs.dirty.next(true);
				this.toastr.success('Witterung abgerufen');
			} else {
				this.toastr.error('Keine Wetterdaten verfügbar');
			}
		} catch (error) {
			console.error('[NaturraumComponent] Fehler beim Abrufen der Witterung:', error);
			this.toastr.error('Fehler beim Abrufen der Witterung');
		}

		this.weatherLoading = false;
	}

	clearWeather() {
		this.data.extendedHeader.witterung = '';
		delete this.data.extendedHeader.witterungRohdaten;
		// Gleiche Regel wie beim Holen: ohne die Marke wurde das Entfernen nur
		// gespeichert, wenn zufaellig noch ein anderes Feld angefasst wurde.
		this.vs.dirty.next(true);
	}

	/**
	 * Defensiv-Init fuer Grube-Sub-Objekte. Angular erlaubt keine Methoden-
	 * Aufrufe in [(model)]-Bindings (kein Setter), deswegen muessen die
	 * AltNeu-/Mosaik-Objekte existieren, bevor das Template sie bindet.
	 */
	override ngDoCheck(): void {
		super.ngDoCheck();
		if (!this.data || !this.isG()) return;
		const eh = this.data.extendedHeader;
		const h = this.data.header?.humusform;
		if (!eh.mosaikBereich) eh.mosaikBereich = { nummer: '', name: '', haupttyp: '' } as MosaikBereich;
		if (!eh.bestockungsTyp) eh.bestockungsTyp = { alt: '', neu: '' } as AltNeu<string>;
		if (!eh.klimabereich) eh.klimabereich = { alt: '', neu: '' } as AltNeu<string>;
		if (eh.stfg) {
			if (!eh.stfg.zustands) eh.stfg.zustands = { alt: '', neu: '' };
			if (!eh.stfg.imissions) eh.stfg.imissions = { alt: '', neu: '' };
		}
		if (h) {
			if (!h.gesamtFlaeche) h.gesamtFlaeche = { alt: '', neu: '' };
			if (!h.morphologisch) h.morphologisch = { alt: '', neu: '' };
		}
	}

	/**
	 * Prüft ob aktuelle Neigungsstufe eine Ebene oder Rampe ist (≤ 3°)
	 * Bei diesen wird die Einbindung in die Nachbarschaft verwendet
	 */
	istEbeneOderRampe(): boolean {
		const stufe = this.data?.extendedHeader?.relief?.neigungsstufe;
		if (!stufe) return true; // Default: Ebene annehmen
		return stufe === 'Ebene' || stufe === 'schwächer geneigter Flachhang';
	}

	/**
	 * Prüft ob die Hangrichtung relevant ist (ab stärker geneigtem Flachhang > 3°)
	 */
	istHangmitRichtung(): boolean {
		const stufe = this.data?.extendedHeader?.relief?.neigungsstufe;
		if (!stufe) return false;
		return stufe !== 'Ebene' && stufe !== 'schwächer geneigter Flachhang';
	}

	/**
	 * Neigungsstufe aus der Bild-Auswahl übernehmen.
	 *
	 * Das frühere Auswahlfeld hat beim Ändern `onNeigungsstufeChange` gerufen;
	 * daran hängen der Grad-Wert und das Löschen der Hangrichtung. Der Picker
	 * meldet nur den neuen Wert, deshalb setzt diese Stelle beides.
	 */
	setzeNeigungsstufe(stufe: string | undefined) {
		if (!this.data?.extendedHeader?.relief) return;
		this.data.extendedHeader.relief.neigungsstufe = (stufe || undefined) as Neigungsstufe | undefined;
		if (stufe) this.onNeigungsstufeChange(stufe);
	}

	/**
	 * Hanganschluss aus der Bild-Auswahl uebernehmen.
	 *
	 * Der Picker meldet eine gewoehnliche Zeichenkette, das Feld fuehrt aber die
	 * vier festen Lagen. Die Umwandlung gehoert deshalb hierher und nicht in die
	 * Vorlage: Dort schlaegt `strictTemplates` zu (TS2322), und ein `$any` in der
	 * Vorlage haette die Pruefung nur stillgelegt.
	 */
	setzeHanganschluss(lage: string | undefined) {
		if (!this.data?.extendedHeader?.relief) return;
		this.data.extendedHeader.relief.hanganschluss = (lage || undefined) as Hanganschluss | undefined;
	}

	/**
	 * Woelbung aus der Bild-Auswahl uebernehmen - derselbe Weg wie beim
	 * Hanganschluss: Der Picker meldet gewoehnlichen Text, das Feld fuehrt eine
	 * feste Werteliste. Aufgefallen erst, als `data` einen Typ bekam
	 * (19.09.2026); bis dahin pruefte die Vorlage hier gar nichts.
	 */
	setzeWoelbung(woelbung: string | undefined) {
		if (!this.data?.extendedHeader?.relief) return;
		this.data.extendedHeader.relief.woelbung = (woelbung || undefined) as Woelbung | undefined;
	}

	/** Einbindung in die Ebene aus der Bild-Auswahl uebernehmen - wie {@link setzeWoelbung}. */
	setzeEinbindungEbene(einbindung: string | undefined) {
		if (!this.data?.extendedHeader?.relief) return;
		this.data.extendedHeader.relief.einbindungEbene = (einbindung || undefined) as EinbindungEbene | undefined;
	}

	/**
	 * Wird aufgerufen wenn sich die Neigungsstufe ändert
	 * Setzt automatisch einen passenden Grad-Wert (Mitte des Bereichs)
	 */
	onNeigungsstufeChange(stufe: string) {
		if (!stufe || !this.data?.extendedHeader?.relief) return;
		const neigungsstufe = stufe as Neigungsstufe;

		const grenzen = NEIGUNGSSTUFEN_GRENZEN.find(g => g.stufe === stufe);
		if (grenzen) {
			// Grad-Wert auf Mitte des Bereichs setzen
			this.data.extendedHeader.relief.neigungGrad = Math.round((grenzen.minGrad + grenzen.maxGrad) / 2);
		}

		// Hangrichtung löschen wenn nicht mehr relevant
		if (!this.istHangmitRichtung()) {
			this.data.extendedHeader.relief.hangrichtung = undefined;
		}
	}

	/**
	 * Wird aufgerufen wenn sich der Neigungsgrad ändert
	 * Passt automatisch die Neigungsstufe an
	 */
	onNeigungGradChange(grad: number) {
		if (grad === undefined || grad === null || !this.data?.extendedHeader?.relief) return;

		const passendeStufe = NEIGUNGSSTUFEN_GRENZEN.find(
			g => grad >= g.minGrad && grad <= g.maxGrad
		);

		if (passendeStufe) {
			this.data.extendedHeader.relief.neigungsstufe = passendeStufe.stufe;
		}
	}
}
